import { Button, Col, Input, Label, Row } from "reactstrap";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import { BsTrash3 } from "react-icons/bs";
import IconButton from "../../../../../components/Buttons/IconButton";
import { useSettings } from "../../../../../context/settings";
import DateTimeInput from "../../../../../components/Forms/DateTimeInput";

const EMPTY_EXPERIENCE = { company_name: '', job_title: '', start_date: '', end_date: '', description: '' };
const EMPTY_EDUCATION = { school_name: '', degree: '', specialization: '', date_of_completion: '' };

function HeaderRow({ columns }) {
  return (
    <Row className="gx-2 mb-1 d-none d-md-flex">
      {columns.map((col, i) => (
        <Col md={col.size} key={i}>
          <Label className="small text-muted mb-0">{col.label}</Label>
        </Col>
      ))}
      <Col md="1" />
    </Row>
  );
}

function WorkExperienceTab({ employeeData, setEmployeeData }) {
  const { t } = useTranslation();
  const { datePickerFormat } = useSettings();

  const experiences = employeeData.work_experiences || [];
  const educations = employeeData.educations || [];

  const addRow = (key, empty) =>
    setEmployeeData((prev) => ({ ...prev, [key]: [...(prev[key] || []), { ...empty }] }));

  const removeRow = (key, index) =>
    setEmployeeData((prev) => ({ ...prev, [key]: (prev[key] || []).filter((_, i) => i !== index) }));

  const updateRow = (key, index, patch) =>
    setEmployeeData((prev) => ({
      ...prev,
      [key]: (prev[key] || []).map((row, i) => (i === index ? { ...row, ...patch } : row)),
    }));

  // Convert a DatePicker value (Date object) to a MySQL-friendly `YYYY-MM-DD`
  // string using local date components, so the day the user picked is preserved
  // (slicing the ISO string would shift the day in non-UTC timezones).
  const toDateString = (value) => (value ? format(new Date(value), 'yyyy-MM-dd') : null);

  return (
    <>
      <div className="mb-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h5 className="fw-bold mb-0">{t('hr.workExperience')}</h5>
          <Button color="primary" size="sm" outline onClick={() => addRow('work_experiences', EMPTY_EXPERIENCE)}>
            + {t('hr.addExperience')}
          </Button>
        </div>

        {experiences.length === 0 ? (
          <p className="text-muted mb-0">{t('hr.noExperience')}</p>
        ) : (
          <>
            <HeaderRow
              columns={[
                { size: '3', label: t('common.companyName') },
                { size: '2', label: t('hr.jobTitle') },
                { size: '2', label: t('common.from') },
                { size: '2', label: t('common.to') },
                { size: '2', label: t('common.description') },
              ]}
            />
            {experiences.map((row, i) => (
              <Row key={i} className="gx-2 mb-2 align-items-center">
                <Col md="3" className="mb-2 mb-md-0">
                  <Input
                    bsSize="sm"
                    placeholder={t('common.companyName')}
                    value={row.company_name || ''}
                    onChange={(e) => updateRow('work_experiences', i, { company_name: e.target.value })}
                  />
                </Col>
                <Col md="2" className="mb-2 mb-md-0">
                  <Input
                    bsSize="sm"
                    placeholder={t('hr.jobTitle')}
                    value={row.job_title || ''}
                    onChange={(e) => updateRow('work_experiences', i, { job_title: e.target.value })}
                  />
                </Col>
                <Col md="2" className="mb-2 mb-md-0">
                  <DateTimeInput 
                    value={row.start_date || null}
                    onChange={(date) => updateRow('work_experiences', i, { start_date: date })}
                    maxDate={row.end_date || undefined}
                  />
                </Col>
                <Col md="2" className="mb-2 mb-md-0">
                  <DateTimeInput
                    value={row.end_date || null}
                    onChange={(date) => updateRow('work_experiences', i, { end_date: date })}
                    minDate={row.start_date || undefined}
                  />
                </Col>
                <Col md="2" className="mb-2 mb-md-0">
                  <Input
                    bsSize="sm"
                    placeholder={t('common.description')}
                    value={row.description || ''}
                    onChange={(e) => updateRow('work_experiences', i, { description: e.target.value })}
                  />
                </Col>
                <Col md="1" className="text-md-end mb-2 mb-md-0">
                  <IconButton onClick={() => removeRow('work_experiences', i)} size="sm" outline color="danger">
                    <BsTrash3 />
                  </IconButton>
                </Col>
              </Row>
            ))}
          </>
        )}
      </div>

      <div>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h5 className="fw-bold mb-0">{t('hr.education')}</h5>
          <Button color="primary" size="sm" outline onClick={() => addRow('educations', EMPTY_EDUCATION)}>
            + {t('hr.addEducation')}
          </Button>
        </div>

        {educations.length === 0 ? (
          <p className="text-muted mb-0">{t('hr.noEducation')}</p>
        ) : (
          <>
            <HeaderRow
              columns={[
                { size: '3', label: t('hr.instituteName') },
                { size: '3', label: t('hr.degree') },
                { size: '3', label: t('hr.specialization') },
                { size: '2', label: t('hr.dateOfCompletion') },
              ]}
            />
            {educations.map((row, i) => (
              <Row key={i} className="gx-2 mb-2 align-items-center">
                <Col md="3" className="mb-2 mb-md-0">
                  <Input
                    bsSize="sm"
                    placeholder={t('hr.instituteName')}
                    value={row.school_name || ''}
                    onChange={(e) => updateRow('educations', i, { school_name: e.target.value })}
                  />
                </Col>
                <Col md="3" className="mb-2 mb-md-0">
                  <Input
                    bsSize="sm"
                    placeholder={t('hr.degree')}
                    value={row.degree || ''}
                    onChange={(e) => updateRow('educations', i, { degree: e.target.value })}
                  />
                </Col>
                <Col md="3" className="mb-2 mb-md-0">
                  <Input
                    bsSize="sm"
                    placeholder={t('hr.specialization')}
                    value={row.specialization || ''}
                    onChange={(e) => updateRow('educations', i, { specialization: e.target.value })}
                  />
                </Col>
                <Col md="2" className="mb-2 mb-md-0">
                  <DatePicker
                    selected={row.date_of_completion || null}
                    onChange={(date) => updateRow('educations', i, { date_of_completion: date })}
                    className="form-control form-control-sm"
                    wrapperClassName="w-100"
                    dateFormat={datePickerFormat}
                    placeholderText={t('hr.dateOfCompletion')}
                  />
                </Col>
                <Col md="1" className="text-md-end mb-2 mb-md-0">
                  <IconButton onClick={() => removeRow('educations', i)} size="sm" outline color="danger">
                    <BsTrash3 />
                  </IconButton>
                </Col>
              </Row>
            ))}
          </>
        )}
      </div>
    </>
  );
}

export default WorkExperienceTab;